import { useEffect, useRef, useState, FormEvent } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { AutoTextarea } from '../components/AutoTextarea';
import { DictateButton } from '../components/DictateButton';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { useUserOrg } from '../lib/org';
import { getSite, listSubcontractors, createRams, getRams, updateRams, type Site } from '../lib/db';
import { uploadRams } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { RAMS_TEMPLATES, BLANK_RAMS_TEMPLATE } from '../data/ramsTemplates';
import { generateRamsTemplatePdf } from '../lib/pdf';
import { useDraftAutosave, loadDraft, clearDraft, formatDraftAge } from '../lib/draftAutosave';
import { LoadingIndicator } from '../components/LoadingIndicator';

type Risk = 'High' | 'Med' | 'Low';

/**
 * Tick-first forms direction (May 2026): RAMS controls are now rendered as
 * a tickable list per hazard rather than a free-text paragraph. The data
 * model carries:
 *   - `controlOptions`: the master list of available control measures for
 *     this hazard, seeded from the template's semicolon-separated string
 *     plus any custom controls the user has added.
 *   - `controlChecks`: one boolean per option — true = "this control
 *     applies on my site".
 * At submit time, `controlOptions.filter(checked).join('; ')` reconstructs
 * the semicolon-separated string the PDF generator expects.
 *
 * Editing the hazard description as free text is preserved because hazard
 * wording is the bit that's genuinely site-specific.
 */
interface HazardDraft {
  risk: Risk;
  hazard: string;
  controlOptions: string[];
  controlChecks: boolean[];
}

/**
 * Split a template's semicolon-separated controls string into individual
 * control statements. Tolerates ". " separators too (older templates),
 * trims whitespace, drops empties.
 */
function parseControls(s: string): string[] {
  return s
    .split(/;\s+|(?<=[a-z0-9)])\.\s+(?=[A-Z])/)
    .map((c) => c.trim().replace(/\.$/, ''))
    .filter((c) => c.length > 0)
    // Sentence-case the first letter so a control like "toe-boards in place"
    // becomes "Toe-boards in place" — RAMS templates were authored with mixed
    // case which looks unprofessional in the generated PDF.
    .map((c) => c.charAt(0).toUpperCase() + c.slice(1));
}

const REVIEW_CHECKS = [
  'Site condition checked — physical conditions match the assumed scope',
  'Utilities checked — gas / electric / water / drainage drawings reviewed and CAT-scanned where relevant',
  'Competence verified — every person on this work has the cards / training to do it',
  'Control measures reviewed — every hazard above has a workable control on this site',
  'I am competent to prepare and sign off this RAMS — I have the relevant training, experience and authority',
];

interface EditableSectionProps {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}

function EditableSection({ label, hint, value, onChange, rows = 5 }: EditableSectionProps) {
  return (
    <div className="rams-section-edit">
      <div className="rams-section-head">
        <span className="rams-section-label">{label}</span>
        <span className="rams-section-pen">✎ Edit required</span>
      </div>
      <p className="rams-section-hint">{hint}</p>
      <div className="input-with-mic">
        <AutoTextarea minRows={rows} value={value} onChange={(e) => onChange(e.target.value)} />
        <DictateButton onTranscript={(t) => onChange(value ? value + ' ' + t : t)} />
      </div>
    </div>
  );
}

/**
 * TickListSection — render a master list of options as tickable rows.
 * Used for Equipment and PPE: the template seeds the list, every item
 * starts ticked, the user unticks what doesn't apply, removes anything
 * they don't want at all, and adds custom items via the "Add" input.
 *
 * The pen icon stays in the section header so it's clear the user can
 * (and should) review this section, but the interaction is tap-not-type.
 */
interface TickListSectionProps {
  label: string;
  hint: string;
  options: string[];
  checks: boolean[];
  customValue: string;
  onCustomChange: (v: string) => void;
  onToggle: (idx: number) => void;
  onRemove: (idx: number) => void;
  onAdd: () => void;
  addPlaceholder: string;
}

function TickListSection({
  label, hint, options, checks, customValue, onCustomChange,
  onToggle, onRemove, onAdd, addPlaceholder,
}: TickListSectionProps) {
  const tickedCount = checks.filter(Boolean).length;
  return (
    <div className="rams-section-edit">
      <div className="rams-section-head">
        <span className="rams-section-label">{label}</span>
        <span className="rams-section-pen">
          {tickedCount} of {options.length} ticked
        </span>
      </div>
      <p className="rams-section-hint">{hint}</p>

      {options.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, margin: '4px 0 8px' }}>
          Nothing in the list yet — add an item below.
        </p>
      ) : (
        <div className="check-list">
          {options.map((item, i) => (
            <label key={`${i}-${item}`} className="check-row">
              <input
                type="checkbox"
                checked={checks[i] ?? false}
                onChange={() => onToggle(i)}
              />
              <span style={{ flex: 1 }}>{item}</span>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); onRemove(i); }}
                aria-label={`Remove ${item}`}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--ink-soft)',
                  fontSize: 18,
                  padding: '0 4px',
                  cursor: 'pointer',
                }}
              >×</button>
            </label>
          ))}
        </div>
      )}

      <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder={addPlaceholder}
            value={customValue}
            onChange={(e) => onCustomChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAdd();
              }
            }}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onAdd}
            disabled={!customValue.trim()}
            style={{ flex: '0 0 auto', padding: '0 16px' }}
          >
            + Add
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * HazardControlsList — tickable list of control measures for a single
 * hazard. Owned by the parent RamsPresetForm state via the controlOptions
 * + controlChecks arrays on the HazardDraft.
 */
interface HazardControlsListProps {
  hazardIdx: number;
  options: string[];
  checks: boolean[];
  onToggle: (hazardIdx: number, controlIdx: number) => void;
  onAdd: (hazardIdx: number, text: string) => void;
  onRemove: (hazardIdx: number, controlIdx: number) => void;
}

function HazardControlsList({
  hazardIdx, options, checks, onToggle, onAdd, onRemove,
}: HazardControlsListProps) {
  const [custom, setCustom] = useState('');
  return (
    <>
      {options.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, margin: '4px 0 8px' }}>
          No control measures yet — add one below.
        </p>
      ) : (
        <div className="check-list" style={{ marginTop: 4 }}>
          {options.map((opt, ci) => (
            <label key={`${ci}-${opt}`} className="check-row">
              <input
                type="checkbox"
                checked={checks[ci] ?? false}
                onChange={() => onToggle(hazardIdx, ci)}
              />
              <span style={{ flex: 1 }}>{opt}</span>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); onRemove(hazardIdx, ci); }}
                aria-label={`Remove control: ${opt}`}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--ink-soft)',
                  fontSize: 18,
                  padding: '0 4px',
                  cursor: 'pointer',
                }}
              >×</button>
            </label>
          ))}
        </div>
      )}

      <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder="Add another control measure…"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAdd(hazardIdx, custom);
                setCustom('');
              }
            }}
            style={{ flex: 1, fontSize: 13 }}
          />
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => { onAdd(hazardIdx, custom); setCustom(''); }}
            disabled={!custom.trim()}
            style={{ flex: '0 0 auto', padding: '0 12px', fontSize: 13 }}
          >
            + Add
          </button>
        </div>
      </div>
    </>
  );
}

async function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

export function RamsPresetForm() {
  const { siteId, templateId } = useParams<{ siteId: string; templateId: string }>();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const { org } = useUserOrg();
  // ramsId is fixed for the duration of this mount. For "Create" we
  // generate a fresh UUID; for "Edit" we keep the existing row's id so
  // the in-place update overwrites the same storage path (and any
  // outstanding 30-day share links keep resolving). The query param
  // `?ramsId=<id>` is the edit signal.
  const editingRamsId = searchParams.get('ramsId');
  const ramsIdRef = useRef<string>(editingRamsId ?? crypto.randomUUID());
  const isEditing = !!editingRamsId;
  const pcSigRef = useRef<SignaturePadHandle>(null);

  const template = templateId === 'blank'
    ? BLANK_RAMS_TEMPLATE
    : RAMS_TEMPLATES.find((t) => t.id === templateId);

  const [site, setSite] = useState<Site | null>(null);
  const [companies, setCompanies] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Site/job
  const [plotOrArea, setPlotOrArea] = useState('');
  const [client, setClient] = useState('');
  const [preparedBy, setPreparedBy] = useState('');

  // Subbie info
  const [company, setCompany] = useState('');
  const [companyContact, setCompanyContact] = useState('');
  const [companyMobile, setCompanyMobile] = useState('');

  // Editable RAMS body
  const [scope, setScope] = useState('');
  const [hazards, setHazards] = useState<HazardDraft[]>([]);
  const [sequenceText, setSequenceText] = useState('');
  // Tick-first: equipment and PPE are now master-lists with selections,
  // not free text. The template seeds the master list and pre-checks
  // every item; the user unticks anything that doesn't apply, and can
  // add custom items via the "Add" input. At submit, we render the
  // selected subset as a newline-separated string for the PDF.
  const [equipmentOptions, setEquipmentOptions] = useState<string[]>([]);
  const [equipmentChecks, setEquipmentChecks] = useState<boolean[]>([]);
  const [equipmentCustom, setEquipmentCustom] = useState('');
  const [ppeOptions, setPpeOptions] = useState<string[]>([]);
  const [ppeChecks, setPpeChecks] = useState<boolean[]>([]);
  const [ppeCustom, setPpeCustom] = useState('');
  const [emergency, setEmergency] = useState('');
  const [notes, setNotes] = useState('');

  const [reviewChecks, setReviewChecks] = useState<boolean[]>([false, false, false, false, false]);

  // Draft autosave (UX review #23) — same shape as FormAccident's autosave.
  // We persist everything serialisable; PC signature canvas + photos are
  // not saved.
  const draftKey = `rams.preset:${siteId ?? 'unknown'}:${templateId ?? 'blank'}:${ramsIdRef.current}`;
  const [restorable, setRestorable] = useState<{ savedAt: number } | null>(null);
  const [restoreBannerDismissed, setRestoreBannerDismissed] = useState(false);

  useEffect(() => {
    const d = loadDraft<Record<string, unknown>>(draftKey);
    if (d) setRestorable({ savedAt: d.savedAt });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const draftPayload = {
    plotOrArea, client, preparedBy, company, companyContact, companyMobile,
    scope, hazards, sequenceText,
    equipmentOptions, equipmentChecks,
    ppeOptions, ppeChecks,
    emergency, notes,
    reviewChecks,
  };
  useDraftAutosave(draftKey, draftPayload, { enabled: !submitting });

  function restoreDraft() {
    const d = loadDraft<typeof draftPayload>(draftKey);
    if (!d) { setRestorable(null); return; }
    const p = d.payload as Record<string, unknown>;
    setPlotOrArea(String(p.plotOrArea ?? '')); setClient(String(p.client ?? ''));
    setPreparedBy(String(p.preparedBy ?? '')); setCompany(String(p.company ?? ''));
    setCompanyContact(String(p.companyContact ?? '')); setCompanyMobile(String(p.companyMobile ?? ''));
    setScope(String(p.scope ?? ''));
    // Hazards migration — drafts saved before the tick-first refactor used
    // `controls: string`. Detect and migrate so a pre-refactor in-flight
    // RAMS can still be restored cleanly.
    const rawHazards = Array.isArray(p.hazards) ? p.hazards as unknown[] : [];
    setHazards(rawHazards.map((h) => {
      const hh = h as Record<string, unknown>;
      const risk = (hh.risk === 'High' || hh.risk === 'Low') ? hh.risk : 'Med';
      const hazard = String(hh.hazard ?? '');
      if (Array.isArray(hh.controlOptions) && Array.isArray(hh.controlChecks)) {
        return {
          risk: risk as Risk,
          hazard,
          controlOptions: (hh.controlOptions as unknown[]).map(String),
          controlChecks: (hh.controlChecks as unknown[]).map(Boolean),
        };
      }
      // Migrate legacy `controls: string` shape.
      const opts = parseControls(String(hh.controls ?? ''));
      return {
        risk: risk as Risk,
        hazard,
        controlOptions: opts,
        controlChecks: opts.map(() => true),
      };
    }));
    setSequenceText(String(p.sequenceText ?? ''));
    // Equipment/PPE: same migration concern — legacy drafts had
    // `equipmentText` / `ppeText` strings.
    if (Array.isArray(p.equipmentOptions) && Array.isArray(p.equipmentChecks)) {
      setEquipmentOptions((p.equipmentOptions as unknown[]).map(String));
      setEquipmentChecks((p.equipmentChecks as unknown[]).map(Boolean));
    } else if (typeof p.equipmentText === 'string') {
      const items = p.equipmentText.split('\n').map((s) => s.trim()).filter(Boolean);
      setEquipmentOptions(items);
      setEquipmentChecks(items.map(() => true));
    }
    if (Array.isArray(p.ppeOptions) && Array.isArray(p.ppeChecks)) {
      setPpeOptions((p.ppeOptions as unknown[]).map(String));
      setPpeChecks((p.ppeChecks as unknown[]).map(Boolean));
    } else if (typeof p.ppeText === 'string') {
      const items = p.ppeText.split('\n').map((s) => s.trim()).filter(Boolean);
      setPpeOptions(items);
      setPpeChecks(items.map(() => true));
    }
    setEmergency(String(p.emergency ?? ''));
    setNotes(String(p.notes ?? ''));
    setReviewChecks(Array.isArray(p.reviewChecks)
      ? (p.reviewChecks as unknown[]).map(Boolean)
      : [false, false, false, false, false]);
    setRestorable(null);
  }

  function discardDraft() {
    clearDraft(draftKey);
    setRestorable(null);
    setRestoreBannerDismissed(true);
  }

  useEffect(() => {
    if (!siteId || !org) return;
    Promise.all([getSite(siteId), listSubcontractors(org.org_id)])
      .then(([s, subs]) => {
        setSite(s);
        if (s?.client_name) setClient(s.client_name);
        setCompanies(subs.map((sub) => sub.company_name));
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
    supabase.auth.getUser().then(({ data }) => {
      const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
      if (name && !preparedBy) setPreparedBy(name);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, org]);

  // Pre-fill body sections from the template. Tick-first conversion:
  //   - Each hazard's `controls` string is split into individual control
  //     statements; all start ticked. User unticks the ones that don't
  //     apply on this site.
  //   - Equipment + PPE seed the master tick-list with everything checked.
  // SKIPPED when we're hydrating an existing RAMS — see the next effect.
  useEffect(() => {
    if (!template) return;
    if (isEditing) return;  // editor path hydrates from form_state instead
    setScope(template.scope);
    setHazards(template.hazards.map((h) => {
      const opts = parseControls(h.controls);
      return {
        risk: h.risk,
        hazard: h.hazard,
        controlOptions: opts,
        controlChecks: opts.map(() => true),
      };
    }));
    setSequenceText(template.sequence.map((s, i) => `${i + 1}. ${s}`).join('\n'));
    setEquipmentOptions([...template.equipment]);
    setEquipmentChecks(template.equipment.map(() => true));
    setPpeOptions([...template.ppe]);
    setPpeChecks(template.ppe.map(() => true));
    setEmergency(template.emergency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  // Hydrate from form_state when editing an existing RAMS. Falls back
  // to the template defaults if the row pre-dates the form_state
  // column (created before this feature shipped) — in which case the
  // user gets an "edit starting from template" experience that's
  // still better than no edit at all.
  useEffect(() => {
    if (!isEditing || !editingRamsId || !template) return;
    let cancelled = false;
    (async () => {
      try {
        const row = await getRams(editingRamsId);
        if (cancelled || !row) return;
        const p = (row.form_state ?? null) as Record<string, unknown> | null;
        if (!p) {
          // Pre-form_state RAMS — at least seed the company from the
          // row's existing fields so the user doesn't have to retype it.
          if (row.company) setCompany(row.company);
          // Then seed body from the template (mirrors the create path).
          setScope(template.scope);
          setHazards(template.hazards.map((h) => {
            const opts = parseControls(h.controls);
            return { risk: h.risk, hazard: h.hazard, controlOptions: opts, controlChecks: opts.map(() => true) };
          }));
          setSequenceText(template.sequence.map((s, i) => `${i + 1}. ${s}`).join('\n'));
          setEquipmentOptions([...template.equipment]);
          setEquipmentChecks(template.equipment.map(() => true));
          setPpeOptions([...template.ppe]);
          setPpeChecks(template.ppe.map(() => true));
          setEmergency(template.emergency);
          return;
        }
        // form_state present — replay the exact ticks the user had.
        setPlotOrArea(String(p.plotOrArea ?? ''));
        setClient(String(p.client ?? ''));
        setPreparedBy(String(p.preparedBy ?? ''));
        setCompany(String(p.company ?? ''));
        setCompanyContact(String(p.companyContact ?? ''));
        setCompanyMobile(String(p.companyMobile ?? ''));
        setScope(String(p.scope ?? ''));
        const rawHazards = Array.isArray(p.hazards) ? p.hazards as unknown[] : [];
        setHazards(rawHazards.map((h) => {
          const hh = h as Record<string, unknown>;
          const risk = (hh.risk === 'High' || hh.risk === 'Low') ? hh.risk : 'Med';
          return {
            risk: risk as Risk,
            hazard: String(hh.hazard ?? ''),
            controlOptions: Array.isArray(hh.controlOptions) ? (hh.controlOptions as unknown[]).map(String) : [],
            controlChecks: Array.isArray(hh.controlChecks) ? (hh.controlChecks as unknown[]).map(Boolean) : [],
          };
        }));
        setSequenceText(String(p.sequenceText ?? ''));
        if (Array.isArray(p.equipmentOptions)) setEquipmentOptions((p.equipmentOptions as unknown[]).map(String));
        if (Array.isArray(p.equipmentChecks)) setEquipmentChecks((p.equipmentChecks as unknown[]).map(Boolean));
        if (Array.isArray(p.ppeOptions)) setPpeOptions((p.ppeOptions as unknown[]).map(String));
        if (Array.isArray(p.ppeChecks)) setPpeChecks((p.ppeChecks as unknown[]).map(Boolean));
        setEmergency(String(p.emergency ?? ''));
        setNotes(String(p.notes ?? ''));
        // The pre-issue review checks deliberately reset to false on
        // edit — the user must re-tick each item to confirm the new
        // version is still good to issue. Audit-friendly.
        setReviewChecks([false, false, false, false, false]);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, editingRamsId, template]);

  function setHazardField<K extends keyof HazardDraft>(idx: number, key: K, val: HazardDraft[K]) {
    setHazards((prev) => prev.map((h, i) => (i === idx ? { ...h, [key]: val } : h)));
  }

  function addHazard() {
    // Custom hazards start with no preset controls — the user adds them
    // via the "Add control" affordance per hazard.
    setHazards((prev) => [
      ...prev,
      { risk: 'Med', hazard: '', controlOptions: [], controlChecks: [] },
    ]);
  }

  function removeHazard(idx: number) {
    setHazards((prev) => prev.filter((_, i) => i !== idx));
  }

  function toggleHazardControl(hazardIdx: number, controlIdx: number) {
    setHazards((prev) =>
      prev.map((h, i) => {
        if (i !== hazardIdx) return h;
        const next = h.controlChecks.slice();
        next[controlIdx] = !next[controlIdx];
        return { ...h, controlChecks: next };
      }),
    );
  }

  function addHazardControl(hazardIdx: number, text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    setHazards((prev) =>
      prev.map((h, i) =>
        i === hazardIdx
          ? {
              ...h,
              controlOptions: [...h.controlOptions, trimmed],
              controlChecks: [...h.controlChecks, true],
            }
          : h,
      ),
    );
  }

  function removeHazardControl(hazardIdx: number, controlIdx: number) {
    setHazards((prev) =>
      prev.map((h, i) =>
        i === hazardIdx
          ? {
              ...h,
              controlOptions: h.controlOptions.filter((_, j) => j !== controlIdx),
              controlChecks: h.controlChecks.filter((_, j) => j !== controlIdx),
            }
          : h,
      ),
    );
  }

  // Equipment / PPE tick handlers — same shape for both lists.
  function toggleEquipment(idx: number) {
    setEquipmentChecks((p) => p.map((v, i) => (i === idx ? !v : v)));
  }
  function removeEquipment(idx: number) {
    setEquipmentOptions((p) => p.filter((_, i) => i !== idx));
    setEquipmentChecks((p) => p.filter((_, i) => i !== idx));
  }
  function addEquipment() {
    const t = equipmentCustom.trim();
    if (!t) return;
    setEquipmentOptions((p) => [...p, t]);
    setEquipmentChecks((p) => [...p, true]);
    setEquipmentCustom('');
  }
  function togglePpe(idx: number) {
    setPpeChecks((p) => p.map((v, i) => (i === idx ? !v : v)));
  }
  function removePpe(idx: number) {
    setPpeOptions((p) => p.filter((_, i) => i !== idx));
    setPpeChecks((p) => p.filter((_, i) => i !== idx));
  }
  function addPpe() {
    const t = ppeCustom.trim();
    if (!t) return;
    setPpeOptions((p) => [...p, t]);
    setPpeChecks((p) => [...p, true]);
    setPpeCustom('');
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!org || !site || !template) return;
    if (!preparedBy.trim()) { setError('"Prepared by" is required.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const ramsId = ramsIdRef.current;
      const dateStr = new Date().toLocaleDateString('en-GB', {
        day: '2-digit', month: 'long', year: 'numeric',
      });

      // Capture both digital signatures (as data URLs)
      let pcSignatureDataUrl: string | null = null;
      if (pcSigRef.current && !pcSigRef.current.isEmpty()) {
        pcSignatureDataUrl = await blobToDataUrl(await pcSigRef.current.toBlob());
      }
      // Convert structured hazards back to the [Risk] text format the PDF
      // parser expects. Controls are filtered to only the ticked options
      // and re-joined with "; " (the format the existing PDF expects).
      const hazardsText = hazards
        .filter((h) => h.hazard.trim())
        .map((h) => {
          const selectedControls = h.controlOptions
            .filter((_, i) => h.controlChecks[i])
            .map((c) => c.trim())
            .filter((c) => c.length > 0)
            .join('; ');
          return `[${h.risk}] ${h.hazard.trim()}\n   Controls: ${selectedControls}`;
        })
        .join('\n\n');

      // Equipment + PPE: serialise the ticked subset as newline-separated
      // text — same format the PDF generator already expects.
      const equipmentText = equipmentOptions
        .filter((_, i) => equipmentChecks[i])
        .join('\n');
      const ppeText = ppeOptions
        .filter((_, i) => ppeChecks[i])
        .join('\n');

      const blob = await generateRamsTemplatePdf({
        orgName: org.org_name,
        orgLogoPath: org.logo_path,
        title: template.title,
        trade: template.trades[0],
        reviewChecks,
        pcSignatureDataUrl,
        pcName: preparedBy.trim(),
        // This is the Generate & save path — by here the user has signed
        // and reviewed. Issued, not a draft. Suppress the yellow banner.
        isDraft: false,
        fields: {
          site_name: site.name,
          plot_or_area: plotOrArea.trim() || '—',
          client: client.trim() || '—',
          company: company.trim() || '—',
          company_contact: companyContact.trim() || '—',
          company_mobile: companyMobile.trim() || '—',
          prepared_by: preparedBy.trim(),
          date: dateStr,
          scope: scope.trim(),
          hazards: hazardsText,
          sequence: sequenceText.trim(),
          equipment: equipmentText.trim(),
          ppe: ppeText.trim(),
          emergency: emergency.trim(),
          notes: notes.trim(),
        },
      });

      const fileName = `${template.id}-${site.name.replace(/\s+/g, '-')}-${dateStr.replace(/\s+/g, '-')}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });
      const path = await uploadRams(org.org_id, ramsId, file);

      // Snapshot the full editable form state so a user hitting "Edit"
      // later resumes from exactly where they left off — every tick,
      // every word. templateId is included so the edit link knows
      // which template route to use.
      const formState = {
        templateId: template.id,
        plotOrArea, client, preparedBy, company, companyContact, companyMobile,
        scope, hazards, sequenceText,
        equipmentOptions, equipmentChecks,
        ppeOptions, ppeChecks,
        emergency, notes,
      };
      const title = `${template.title}${plotOrArea.trim() ? ` — ${plotOrArea.trim()}` : ''}`;
      if (isEditing) {
        await updateRams(ramsId, {
          title,
          company: company.trim() || null,
          trade_preset: template.trades[0],
          hazard_presets: template.defaultHazards,
          notes: notes.trim() || null,
          file_name: fileName,
          size_bytes: file.size,
          form_state: formState,
        });
      } else {
        await createRams({
          id: ramsId,
          orgId: org.org_id,
          siteId: site.id,
          title,
          company: company.trim() || null,
          trade_preset: template.trades[0],
          task_preset: null,
          hazard_presets: template.defaultHazards,
          notes: notes.trim() || null,
          file_url: path,
          file_name: fileName,
          mime_type: 'application/pdf',
          size_bytes: file.size,
          form_state: formState,
        });
      }

      // Saved successfully — purge the autosave draft so the next visit
      // doesn't offer to restore a now-stale copy.
      clearDraft(draftKey);

      // Land on the list view with ?share=<id> so the ShareModal auto-opens
      // and the user sees their newly-saved RAMS in context before
      // emailing it on.
      nav(`/sites/${site.id}/rams/list?share=${ramsIdRef.current}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  if (loading) {
    return <AppShell title="DRAFT RAMS" back={`/sites/${siteId ?? ''}/rams/presets`}><LoadingIndicator /></AppShell>;
  }
  if (!site || !template) {
    return <AppShell title="DRAFT RAMS" back={`/sites/${siteId ?? ''}/rams/presets`}><div className="banner banner-error">{error ?? 'Template not found.'}</div></AppShell>;
  }

  return (
    <AppShell title={template.title} back={`/sites/${site.id}/rams/presets`}>
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0 }}><strong>Template:</strong> {template.description}</p>
        <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--ink-soft)' }}>
          Trade: <strong>{template.trades.join(' / ')}</strong> · {template.hazards.length} standard hazards · {template.sequence.length} sequence steps
        </p>
      </div>

      <div className="rams-callout">
        <strong>↓ Sections marked ✎ Edit required.</strong> Adjust anything that doesn't fit
        this site — wording, hazards, sequence, equipment, PPE. The pink/yellow tint = needs your input.
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {restorable && !restoreBannerDismissed && (
        <div
          className="card"
          style={{
            background: '#FFF8E5',
            borderColor: 'var(--warn, #BF8E3D)',
            marginBottom: 12,
          }}
          role="status"
        >
          <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
            Pick up where you left off?
          </p>
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            We saved a draft of this RAMS {formatDraftAge(restorable.savedAt)}. Restore it or start fresh.
          </p>
          <div className="btn-stack" style={{ gap: 8, flexDirection: 'row' }}>
            <button type="button" className="btn btn-primary" onClick={restoreDraft}>
              Restore my draft
            </button>
            <button type="button" className="btn btn-ghost" onClick={discardDraft}>
              Start fresh
            </button>
          </div>
        </div>
      )}

      <form onSubmit={onSubmit}>
        <h3 className="section-title">Site & job</h3>

        <div className="field needs-input">
          <label htmlFor="r-plot">Plot or area on site</label>
          <input id="r-plot" type="text" placeholder="e.g. Plot 1 east elevation" value={plotOrArea} onChange={(e) => setPlotOrArea(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="r-client">Client</label>
          <input id="r-client" type="text" value={client} onChange={(e) => setClient(e.target.value)} />
        </div>
        <div className="field needs-input">
          <label htmlFor="r-by">Prepared by *</label>
          <input id="r-by" type="text" required value={preparedBy} onChange={(e) => setPreparedBy(e.target.value)} />
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Contractor / company doing the work</h3>

        <div className="field needs-input">
          <label htmlFor="r-company">Company</label>
          <input id="r-company" type="text" list="known-companies-presets" placeholder="e.g. ACME Scaffolding, or your own org" value={company} onChange={(e) => setCompany(e.target.value)} />
          <datalist id="known-companies-presets">
            {companies.map((c) => <option key={c} value={c} />)}
          </datalist>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="r-co-contact">Main contact</label>
            <input id="r-co-contact" type="text" value={companyContact} onChange={(e) => setCompanyContact(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="r-co-mobile">Mobile</label>
            <input id="r-co-mobile" type="tel" value={companyMobile} onChange={(e) => setCompanyMobile(e.target.value)} />
          </div>
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>RAMS body — review and edit</h3>

        <EditableSection
          label="1. Scope of work"
          hint="The standard scope is below. Add anything specific to this site or this job."
          value={scope}
          onChange={setScope}
          rows={4}
        />

        {/* Structured hazards editor */}
        <div className="rams-section-edit">
          <div className="rams-section-head">
            <span className="rams-section-label">2. Hazards & controls</span>
            <span className="rams-section-pen">✎ Edit required</span>
          </div>
          <p className="rams-section-hint">
            Tap a risk button to set the level. Edit the description and controls. Add or remove hazards as needed.
          </p>

          {hazards.map((h, i) => (
            <div key={i} className="hazard-card">
              <div className="hazard-card-head">
                <div className="risk-buttons" role="radiogroup" aria-label={`Risk for hazard ${i + 1}`}>
                  {(['High', 'Med', 'Low'] as Risk[]).map((r) => (
                    <button
                      key={r}
                      type="button"
                      role="radio"
                      aria-checked={h.risk === r}
                      className={`risk-btn risk-${r.toLowerCase()} ${h.risk === r ? 'active' : ''}`}
                      onClick={() => setHazardField(i, 'risk', r)}
                    >
                      {r}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => removeHazard(i)}
                  className="hazard-remove"
                  aria-label={`Remove hazard ${i + 1}`}
                >×</button>
              </div>
              <div className="field" style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 11 }}>Hazard</label>
                <div className="input-with-mic">
                  <AutoTextarea
                    minRows={2}
                    value={h.hazard}
                    onChange={(e) => setHazardField(i, 'hazard', e.target.value)}
                  />
                  <DictateButton
                    onTranscript={(t) => setHazardField(i, 'hazard', h.hazard ? h.hazard + ' ' + t : t)}
                  />
                </div>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: 11 }}>
                  Controls — tick what applies on your site
                </label>
                <HazardControlsList
                  hazardIdx={i}
                  options={h.controlOptions}
                  checks={h.controlChecks}
                  onToggle={toggleHazardControl}
                  onAdd={addHazardControl}
                  onRemove={removeHazardControl}
                />
              </div>
            </div>
          ))}

          <button type="button" className="btn btn-ghost" onClick={addHazard} style={{ marginTop: 8 }}>
            + Add hazard
          </button>
        </div>

        <EditableSection
          label="3. Method / sequence of work"
          hint="One step per line, numbered. Edit the order or wording for this specific job."
          value={sequenceText}
          onChange={setSequenceText}
          rows={10}
        />

        <TickListSection
          label="4. Equipment required"
          hint="The kit you'll bring on site. Untick anything that doesn't apply, add anything missing."
          options={equipmentOptions}
          checks={equipmentChecks}
          customValue={equipmentCustom}
          onCustomChange={setEquipmentCustom}
          onToggle={toggleEquipment}
          onRemove={removeEquipment}
          onAdd={addEquipment}
          addPlaceholder="e.g. Site dumper, abrasive saw…"
        />

        <TickListSection
          label="5. PPE required"
          hint="The protective kit each worker needs. Untick anything that doesn't apply, add anything missing."
          options={ppeOptions}
          checks={ppeChecks}
          customValue={ppeCustom}
          onCustomChange={setPpeCustom}
          onToggle={togglePpe}
          onRemove={removePpe}
          onAdd={addPpe}
          addPlaceholder="e.g. Cut-resistant gloves Level 5…"
        />

        <EditableSection
          label="6. Emergency arrangements"
          hint="Update with the actual A&E address, on-site first aiders, defib location, evacuation muster point for this site."
          value={emergency}
          onChange={setEmergency}
          rows={4}
        />

        <div className="field" style={{ marginTop: 16 }}>
          <label htmlFor="r-notes">Site-specific notes (optional)</label>
          <div className="input-with-mic">
            <AutoTextarea id="r-notes" minRows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            <DictateButton onTranscript={(t) => setNotes((p) => (p ? p + ' ' + t : t))} />
          </div>
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Pre-use review</h3>
        <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
          Tick what you've personally verified. Your tickmarks are baked into the PDF.
        </p>
        <div className="check-list">
          {REVIEW_CHECKS.map((label, i) => (
            <label key={i} className="check-row">
              <input
                type="checkbox"
                checked={reviewChecks[i]}
                onChange={(e) => {
                  const next = reviewChecks.slice();
                  next[i] = e.target.checked;
                  setReviewChecks(next);
                }}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Your signature</h3>
        <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>
          Sign here as the person who prepared this RAMS. The subbie / contractor counter-signs
          when they're inducted.
        </p>
        <SignaturePad ref={pcSigRef} label="Sign as preparer" />

        <div className="card" style={{ background: 'var(--brand-pale)', borderColor: 'var(--brand-light)', marginTop: 16 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--brand-deep)' }}>
            <strong>What gets generated:</strong> a multi-page PDF with cover info, scope, hazards
            (with risk levels), sequence of work, equipment, PPE, emergency arrangements,
            tickbox checklist, your signature and the disclaimer.
          </p>
        </div>

        <div className="btn-stack">
          <button type="submit" className="btn btn-primary" disabled={submitting || !preparedBy.trim()}>
            {submitting ? 'Generating…' : 'Generate & save RAMS'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => nav(`/sites/${site.id}/rams/presets`)}>
            Cancel
          </button>
        </div>
      </form>
    </AppShell>
  );
}
